import {
  type DragEventHandler,
  type HTMLAttributes,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type Ref,
  type RefObject,
  type TouchEvent as ReactTouchEvent,
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import cx from 'classnames'
import { Icon } from '@/components/Icon'
import { useOnClickOutside } from '@/hooks/click-outside-hooks'
import { useCssTransition } from '@/hooks/transition-hooks'
import { useHotkey } from '@/hooks/keyboard-hooks'
import { useIsMobile, useViewportSize } from '@/hooks/viewport-hooks'
import { match } from '@/utils/pattern-match-util'
import styles from './Dropdown.module.css'
import { useElementSize } from '@/hooks/element-hooks'
import {
  ModalSheet,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetContent,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import {
  type Placement,
  useFloating,
  offset,
  autoUpdate,
  flip,
} from '@floating-ui/react'
import { Portal } from '@/components/Portal'
import { useComposedRefs } from '@radix-ui/react-compose-refs'

export type RenderChildren = (actions: { toggle: () => unknown }) => ReactNode

/**
 * How tightly a dropdown's rows are set, so editors rendered inside one can
 * match it.
 */
export type DropdownDensity = 'default' | 'compact'

const DropdownDensityContext = createContext<DropdownDensity>('default')

/**
 * The density of the dropdown the component is rendered in.
 */
export function useDropdownDensity() {
  return useContext(DropdownDensityContext)
}

type Event = ReactMouseEvent | ReactTouchEvent

type DropdownContentAlignment = 'left' | 'center' | 'right'

type DropdownWidth = 'default' | 'medium' | 'small' | 'miniscule'

interface DropdownProps {
  autoDismiss?: boolean
  button?: ReactNode
  isOpen?: boolean
  isDisabled?: boolean
  onButtonPress?: () => unknown
  onRequestClose?: () => unknown
  position?: Placement
  contentOverButton?: boolean
  allowOverflow?: boolean
  children: RenderChildren | ReactNode
  minWidth?: number
  width?: DropdownWidth
  /**
   * Tighter rows with small icons, for short menus of actions.
   */
  compact?: boolean
  actionCompact?: boolean
  alignContent?: DropdownContentAlignment
  noBottomPadding?: boolean
  testId?: string
}

function alignDropdownContent(align: DropdownContentAlignment) {
  return match(align, {
    left: () => styles.alignLeft,
    center: () => '',
    right: () => styles.alignRight,
  })
}

/**
 * A button that toggles a floating menu, shown as a modal sheet on phones.
 */
export function Dropdown({
  autoDismiss,
  button,
  children,
  isOpen,
  onButtonPress,
  onRequestClose,
  position = 'bottom-end',
  isDisabled,
  contentOverButton,
  allowOverflow,
  testId,
  minWidth,
  width = 'default',
  alignContent = 'center',
  noBottomPadding,
  compact,
}: DropdownProps) {
  const isModalSheet = useIsMobile()

  const [buttonEl, setButtonEl] = useState<HTMLElement | null>(null)
  const dropdownRef = useRef<HTMLElement | null>(null)
  const buttonRef = useRef<HTMLElement | null>(null)
  useLayoutEffect(() => {
    buttonRef.current = buttonEl
  }, [buttonEl])

  const [internalIsOpen, setInternalIsOpen] = useState(false)

  const isControlled = isOpen !== undefined

  const close = useCallback(() => {
    if (isControlled) {
      if (onRequestClose && isOpen) {
        onRequestClose()
      }
      return
    }

    if (autoDismiss) {
      setInternalIsOpen(false)
    }
  }, [autoDismiss, isOpen, onRequestClose, isControlled])

  const closeModalSheet = useCallback(() => {
    if (isControlled) {
      if (onRequestClose && isOpen) {
        onRequestClose()
      }
      return
    }

    setInternalIsOpen(false)
  }, [isOpen, onRequestClose, isControlled])

  const onClickOutside = useCallback(
    (event: MouseEvent | TouchEvent) => {
      // The modal sheet handles its own dismissal.
      if (isModalSheet) {
        return
      }

      if (
        !autoDismiss &&
        (!dropdownRef.current ||
          dropdownRef.current.contains(event.target as Node))
      ) {
        return
      }

      close()
    },
    [isModalSheet, autoDismiss, close],
  )

  useHotkey(['Escape'], close)
  useOnClickOutside(buttonRef, onClickOutside)

  const toggleDropdown = useCallback(() => {
    setInternalIsOpen(!internalIsOpen)
  }, [internalIsOpen])

  const onClickCatchall = useCallback(
    (event?: Event) => {
      event?.preventDefault()
      event?.stopPropagation()
      if (!isControlled) {
        toggleDropdown()
      } else if (onButtonPress) {
        onButtonPress()
      }
    },
    [toggleDropdown, onButtonPress, isControlled],
  )

  const dropdownIsOpen =
    !isDisabled && (!isControlled ? internalIsOpen : isOpen)

  const renderChildren = () =>
    typeof children === 'function'
      ? (children as RenderChildren)({
          toggle: toggleDropdown,
        })
      : children

  return (
    <div className={styles.dropdown}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- wraps the trigger element; keyboard activation of that element bubbles its click here */}
      <div
        className={cx(
          styles.dropdownButtonContainer,
          alignDropdownContent(alignContent),
        )}
        ref={setButtonEl}
        onClick={onClickCatchall}
        data-test={testId}
      >
        {button}
      </div>

      {isModalSheet ? (
        <ModalSheet showing={Boolean(dropdownIsOpen)} onRequestClose={close}>
          <ModalSheetContent>
            <ul className={styles.dropdownSheetContent}>{renderChildren()}</ul>
          </ModalSheetContent>

          <ModalSheetFooter>
            <ModalSheetFooterButtonGroup>
              <Button
                label="Close"
                onPress={closeModalSheet}
                isDisabled={isDisabled}
              />
            </ModalSheetFooterButtonGroup>
          </ModalSheetFooter>
        </ModalSheet>
      ) : (
        <InlineDropdownContent
          renderChildren={renderChildren}
          dropdownIsOpen={dropdownIsOpen}
          allowOverflow={allowOverflow}
          contentOverButton={contentOverButton}
          position={position}
          buttonEl={buttonEl}
          dropdownRef={dropdownRef}
          minWidth={minWidth}
          width={width}
          noBottomPadding={noBottomPadding}
          compact={compact}
        />
      )}
    </div>
  )
}

interface InlineDropdownContentProps {
  dropdownIsOpen?: boolean
  renderChildren: () => ReactNode
  allowOverflow?: boolean
  contentOverButton?: boolean
  position?: DropdownProps['position']
  buttonEl?: HTMLElement | null
  dropdownRef: RefObject<HTMLElement | null>
  minWidth?: number
  width?: DropdownWidth
  noBottomPadding?: boolean
  compact?: boolean
}

function InlineDropdownContent({
  renderChildren,
  dropdownIsOpen,
  allowOverflow,
  contentOverButton,
  position,
  buttonEl,
  dropdownRef,
  minWidth,
  width = 'default',
  noBottomPadding,
  compact,
}: InlineDropdownContentProps) {
  const {
    refs,
    strategy,
    x: left,
    y: top,
  } = useFloating({
    // Fixed, so a menu hanging past the viewport cannot grow the document:
    // the scrollbars that adds shrink the viewport, which flips the menu back
    // and removes them, every frame.
    strategy: 'fixed',
    placement: position,
    // flip() only accounts for the offset when it runs after it.
    middleware: [offset(10), flip()],
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, { animationFrame: true }),
    elements: {
      reference: buttonEl,
    },
  })

  const isPositionTop = position?.includes('top')
  const [transitionRef, rendering, hidden] = useCssTransition(!!dropdownIsOpen)
  const mergedRef = useComposedRefs(
    dropdownRef,
    refs.setFloating,
    transitionRef,
  )

  return (
    <Portal>
      {rendering && (
        <ul
          ref={mergedRef}
          className={cx(
            styles.dropdownContent,
            allowOverflow && styles.allowOverflow,
            contentOverButton && styles.contentOverButton,
            noBottomPadding && styles.noBottomPadding,
            compact && styles.compact,
            isPositionTop ? styles.top : styles.bottom,
            hidden && styles.hidden,
            styles[width],
          )}
          style={{
            position: strategy,
            minWidth,
            top: top ?? 0,
            left: left ?? 0,
          }}
        >
          <DropdownDensityContext value={compact ? 'compact' : 'default'}>
            {renderChildren()}
          </DropdownDensityContext>
        </ul>
      )}
    </Portal>
  )
}

export type CheckboxState = 'checked' | 'unchecked' | 'indeterminate'

interface DropdownItemProps {
  active?: boolean
  checkboxState?: CheckboxState
  checkmark?: boolean
  relative?: boolean
  isDisabled?: boolean
  shortcut?: string
  draggable?: boolean
  isDragging?: boolean
  onPress?: () => unknown
  icon?: ReactNode
  iconRight?: ReactNode
  children?: ReactNode
  role?: string
  tabIndex?: number
  onDragStart?: DragEventHandler<HTMLLIElement>
  muteClickEvent?: boolean
  ref?: Ref<HTMLLIElement>
  testId?: string

  /**
   * Test ID for the item's button, for E2E selectors that target the button
   * rather than the item.
   */
  buttonTestId?: string
}

/**
 * A dropdown item.
 */
export function DropdownItem({
  active,
  checkboxState,
  checkmark,
  children,
  relative,
  isDisabled,
  icon,
  iconRight,
  shortcut,
  draggable,
  isDragging,
  onPress,
  muteClickEvent = false,
  testId,
  buttonTestId,
  ref,
  ...other
}: DropdownItemProps) {
  const onClickCatchall = useCallback(
    (e?: Event) => {
      if (muteClickEvent) {
        e?.stopPropagation()
      }
      if (onPress) {
        onPress()
      }
    },
    [muteClickEvent, onPress],
  )

  return (
    <li className={styles.dropdownItem} ref={ref} data-test={testId} {...other}>
      <button
        type="button"
        className={cx(
          styles.dropdownItemContent,
          active && styles.active,
          isDisabled && !checkboxState && styles.disabled,
          relative && styles.relative,
          draggable && styles.draggable,
          isDragging && styles.isDragging,
        )}
        disabled={isDisabled}
        onClick={onClickCatchall}
        data-test={buttonTestId}
      >
        {draggable && <Icon name="drag" className={styles.dragIcon} />}
        {icon && <div className={styles.dropdownItemIcon}>{icon}</div>}
        <div className={styles.dropdownItemText}>{children}</div>

        {checkboxState && (
          <div
            className={cx(
              styles.dropdownItemCheckbox,
              checkboxState !== 'unchecked' && styles.checked,
              isDisabled && styles.disabled,
            )}
          >
            {match(checkboxState, {
              checked: () => <Icon name="checkbox" />,
              unchecked: () => <Icon name="checkbox-blank" />,
              indeterminate: () => <Icon name="checkbox-indeterminate" />,
            })}
          </div>
        )}

        {checkmark && active && (
          <div className={styles.dropdownItemCheckmark}>
            <Icon auto name="tick" />
          </div>
        )}
        {iconRight && (
          <div className={styles.dropdownItemIconRight}>{iconRight}</div>
        )}
        {shortcut && (
          <div className={styles.dropdownItemShortcut}>{shortcut}</div>
        )}
      </button>
    </li>
  )
}

interface DropdownItemTitleProps extends HTMLAttributes<HTMLParagraphElement> {}

/**
 * The primary line of a two-line dropdown item.
 */
export function DropdownItemTitle({ ...rest }: DropdownItemTitleProps) {
  return <p className={styles.dropdownItemTitle} {...rest} />
}

interface DropdownItemDescriptionProps extends HTMLAttributes<HTMLParagraphElement> {}

/**
 * The secondary line of a two-line dropdown item.
 */
export function DropdownItemDescription({
  ...rest
}: DropdownItemDescriptionProps) {
  return <p className={styles.dropdownItemDescription} {...rest} />
}

interface DropdownTitleProps {
  children: ReactNode
}

/**
 * A title for a section within the dropdown.
 */
export function DropdownTitle({ children }: DropdownTitleProps) {
  return (
    <div className={styles.dropdownTitle}>
      <p className={styles.dropdownTitleText}>{children}</p>
    </div>
  )
}

/**
 * A horizontal rule between groups of dropdown items.
 */
export function DropdownDivider() {
  return (
    <div className={styles.dropdownDivider}>
      <hr />
    </div>
  )
}

interface DropdownScrollProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'className'
> {
  children?: ReactNode
  autoHeightSize?: boolean
  ref?: Ref<HTMLDivElement>
}

/**
 * Caps the dropdown's height and scrolls its items, fading out the bottom
 * edge when they overflow.
 */
export function DropdownScroll({
  children,
  autoHeightSize = false,
  ref,
  ...rest
}: DropdownScrollProps) {
  const { size, connectRef: sizeRef } = useElementSize()
  const composedRef = useComposedRefs(ref, sizeRef)
  const screenHeight = useViewportSize().height
  const isModalSheet = useIsMobile()
  const isCompact = useDropdownDensity() === 'compact'

  const isLong = size.height >= 320

  const maxHeight = useMemo(() => {
    if (isModalSheet) {
      return 'none'
    }

    if (isCompact) {
      return '220px'
    }

    if (autoHeightSize) {
      const delta = screenHeight <= 568 ? 2 : 4

      const calculatedMaxHeight =
        Math.floor((screenHeight - 168) / 50) * 50 + 50 / delta

      const maxHeight = Math.max(calculatedMaxHeight, 40)
      return `${maxHeight}px`
    }

    return '320px'
  }, [autoHeightSize, isCompact, isModalSheet, screenHeight])

  const dropdownGradientHeight = useMemo(() => {
    if (isModalSheet) {
      return 0
    }

    if (autoHeightSize) {
      return '20px'
    }

    return '15px'
  }, [autoHeightSize, isModalSheet])

  return (
    <div
      className={cx(styles.dropdownScroll, isLong && styles.scrollbarPadding)}
      {...rest}
    >
      <div
        ref={composedRef}
        className={cx(styles.dropdownScrollZone)}
        style={{ maxHeight }}
      >
        {children}
        {(isLong || autoHeightSize) && (
          <div
            className={cx(styles.dropdownGradient)}
            style={{ height: dropdownGradientHeight }}
          />
        )}
      </div>
    </div>
  )
}

export interface DropdownItemListProps {
  children: ReactNode
  className?: string
}

/**
 * Dropdown items in compact rows, for lists shown inside another surface
 * rather than in a dropdown of their own.
 */
export function DropdownItemList({
  children,
  className,
}: DropdownItemListProps) {
  return (
    <ul className={cx(styles.compactItems, className)}>
      <DropdownDensityContext value="compact">
        {children}
      </DropdownDensityContext>
    </ul>
  )
}
